import React from 'react';
import { Button, Card, HTMLTable, InputGroup, Popover, PopupKind } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { LegendItem } from './SimulationGraph';
import { describeTransform, IDENTITY_TRANSFORM, isIdentityTransform, SignalTransform, ValueRange } from './signalTransform';
import { SignalTransformEditor } from './SignalTransformEditor';

export interface Parameter {
  name: string;
  value: string;
}

interface ParametersCardProps {
  width: number;
  simulationParameters: Parameter[];
  // Omit to show the parameter values read-only.
  onParameterChange?: (name: string, newValue: string) => void;
  legendItems: LegendItem[];
  traceVisibility: Record<string, boolean>;
  onToggleTrace: (name: string) => void;
  onIsolateTrace: (name: string) => void;
  // How each signal is shifted and scaled on the 2D graph; omit to hide the
  // controls.
  transforms?: Record<string, SignalTransform>;
  onTransformChange?: (name: string, transform: SignalTransform) => void;
  transformRanges?: Map<string, ValueRange>;
  // Legend name of the input whose points are being edited.
  editingTraceName?: string | null;
  // The signal being shifted and scaled directly on the graph.
  adjustingSignal?: string | null;
  onAdjustToggle?: (name: string) => void;
  // Replaces the signal legend in the views that choose their own axes.
  axisSelector?: React.ReactNode;
  // Editor for the input series sent with the parameters.
  inputEditor?: React.ReactNode;
  // Hides the parameter and input editors, leaving only the legend or axes.
  hideEditors?: boolean;
}

// This card is now a pure editor view. SimulationResults owns the parameter
// state so the side panel, footer fields, validation, dirty tracking, and save
// button all react to the same source of truth.
export const ParametersCard: React.FC<ParametersCardProps> = ({
  width,
  simulationParameters,
  onParameterChange,
  legendItems,
  traceVisibility,
  onToggleTrace,
  onIsolateTrace,
  transforms,
  onTransformChange,
  transformRanges,
  editingTraceName = null,
  adjustingSignal = null,
  onAdjustToggle,
  axisSelector,
  inputEditor,
  hideEditors = false,
}) => {
  return (
    <Card
      className="cm-params-card"
      style={{ '--cm-params-width': `${width}px` } as React.CSSProperties}
    >
        {/* Graph legend: click a signal to show or hide it, double-click to
            show only that signal (or everything again) */}
        {axisSelector ?? (legendItems.length > 0 && (
          <div className="cm-legend">
            <h2 className="cm-section-title">Signals</h2>
            <ul className="cm-legend-list">
              {legendItems.map((item) => {
                const isVisible = traceVisibility[item.name] !== false;
                const transform = transforms?.[item.name] ?? IDENTITY_TRANSFORM;
                const isTransformed = !isIdentityTransform(transform);

                return (
                  <li key={item.name} className="cm-legend-row">
                    <button
                      type="button"
                      className={`cm-legend-item${isVisible ? '' : ' is-hidden'}`}
                      onClick={() => onToggleTrace(item.name)}
                      onDoubleClick={() => onIsolateTrace(item.name)}
                      aria-pressed={isVisible}
                      title={`${isVisible ? 'Hide from graph' : 'Show in graph'} (double-click to isolate)`}
                    >
                      <span className={`cm-legend-swatch is-${item.dash}`} style={{ color: item.color }} />
                      <span className="cm-legend-text">
                        <span className="cm-legend-name">{item.name}</span>
                        {isTransformed && <span className="cm-legend-transform">{describeTransform(transform)}</span>}
                      </span>
                    </button>
                    {onAdjustToggle && (
                      <Button
                        small
                        minimal
                        icon={IconNames.Move}
                        active={item.name === adjustingSignal}
                        className={`cm-legend-transform-button${item.name === adjustingSignal ? ' is-active' : ''}`}
                        title="Drag on the graph to shift, scroll to scale"
                        aria-label={`Adjust ${item.name} on the graph`}
                        aria-pressed={item.name === adjustingSignal}
                        onClick={() => onAdjustToggle(item.name)}
                      />
                    )}
                    {transforms && onTransformChange && transformRanges && (
                      <Popover
                        placement="left-start"
                        popupKind={PopupKind.DIALOG}
                        content={(
                          <SignalTransformEditor
                            name={item.name}
                            transform={transform}
                            onChange={(next) => onTransformChange(item.name, next)}
                            ranges={transformRanges}
                            isEditing={item.name === editingTraceName}
                          />
                        )}
                      >
                        <Button
                          small
                          minimal
                          icon={IconNames.Settings}
                          className={`cm-legend-transform-button${isTransformed ? ' is-active' : ''}`}
                          title="Shift or scale on the graph"
                          aria-label={`Shift or scale ${item.name} on the graph`}
                        />
                      </Popover>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {/* Simulation Parameters Section */}
        {!hideEditors && <div>
          <h2 className="cm-section-title">Simulation Parameters</h2>
          <HTMLTable striped compact style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Parameter</th>
                <th align='right'>Value</th>
              </tr>
            </thead>
            <tbody>
              {simulationParameters.map((parameter, index) => (
                <tr key={parameter.name}>
                  <td>{parameter.name}</td>
                  <td align='right'>
                    {onParameterChange ? (
                      <InputGroup
                        value={parameter.value}
                        onChange={(e) => onParameterChange(parameter.name, e.target.value)}
                        placeholder="Enter value"
                        style={{ width: '80px' }}
                      />
                    ) : parameter.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </HTMLTable>
        </div>}

        {!hideEditors && inputEditor}

        {/* CppModel Parameters Section
        <div style={{ marginTop: '20px' }}>
          <H5>Workspace Parameters</H5>
          <table className="bp4-html-table bp4-html-table-striped bp4-html-table-bordered" style={{ width: '100%', marginTop: '10px' }}>
            <thead>
              <tr>
                <th>Parameter</th>
                <th align='right'>Value</th>
              </tr>
            </thead>
            <tbody>
              {workspaceParameters.map((parameter, index) => (
                <tr key={index}>
                  <td>{parameter.name}</td>
                  <td align='right'>
                    <InputGroup
                      value={parameter.value}
                      onChange={(e) => handleParameterChange(setWorkspaceParameters, index, e.target.value)}
                      placeholder="Enter value"
                      style={{ width: '80px' }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div> */}
    </Card>
  );
};
